// Brand selection for the sign-in UI (ADR-0010: runtime branding).
//
// One deployment serves all brands; the brand is resolved at runtime:
//   1. `?brand=onecareer|chapternext` URL override (screenshots, QA previews).
//   2. Tenant id from the URL (IAP `tid` / action-link `tenantId`) mapped
//      through config/tenants.json.
//   3. DEV only: VITE_BRAND from .env (no fallback — fails loud when unset,
//      same as everywhere else). Dead-code eliminated in prod builds.
//   4. Controlled failure value (missing-tenant | unknown-tenant): the app
//      renders an error screen instead of guessing a brand.
//
// The result is computed once and cached so brand assets stay stable even
// after gcip-iap mutates window.location during the sign-in handshake.

import { readTenantIdFromUrl, tenantConfigFor } from "./tenants";

export type Brand = "onecareer" | "chapternext";

export type BrandResolution =
  | { ok: true; brand: Brand; tenantId: string | null }
  | {
      ok: false;
      reason: "missing-tenant" | "unknown-tenant";
      tenantId: string | null;
    };

function normalizeBrand(value: string | null | undefined): Brand | null {
  return value === "onecareer" || value === "chapternext" ? value : null;
}

let cached: BrandResolution | null = null;

export function resolveBrand(): BrandResolution {
  if (!cached) cached = computeResolution();
  return cached;
}

function computeResolution(): BrandResolution {
  const tenantId = readTenantIdFromUrl();

  // QA override wins so previews work on any deployment; the tenant id is
  // still captured for back-to-login.
  const override = normalizeBrand(
    new URLSearchParams(window.location.search).get("brand"),
  );
  if (override) return { ok: true, brand: override, tenantId };

  const config = tenantConfigFor(tenantId);
  if (config) return { ok: true, brand: config.brand, tenantId };

  if (tenantId) {
    console.error(
      `lernwelt-login: tenant id '${tenantId}' is not in config/tenants.json — refusing to serve.`,
    );
    return { ok: false, reason: "unknown-tenant", tenantId };
  }

  if (import.meta.env.DEV) {
    const devBrand = normalizeBrand(import.meta.env.VITE_BRAND);
    if (!devBrand) {
      throw new Error(
        "lernwelt-login DEV: no tenant id in the URL and VITE_BRAND is not " +
          "set to 'onecareer' | 'chapternext'. Set it in .env or use ?brand=. " +
          "There is deliberately no default.",
      );
    }
    console.info(
      `lernwelt-login DEV: no tenant id in URL, using brand '${devBrand}' (VITE_BRAND).`,
    );
    return { ok: true, brand: devBrand, tenantId: null };
  }

  return { ok: false, reason: "missing-tenant", tenantId: null };
}

/** Active brand; throws when resolution failed — call only on the ok path. */
export function detectBrandName(): Brand {
  const res = resolveBrand();
  if (!res.ok) {
    throw new Error(`Brand not resolved (${res.reason}).`);
  }
  return res.brand;
}
